import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { endPoints } from '../../Config';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePutRequest } from '../../service/PutTemplate';

const EditRole = () => {
    const [loadingBtn, setLoadingBtn] = useState(false);
    const [allPermissions, setAllPermissions] = useState(null);
    const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);

    const location = useLocation();
    const { rowData } = location.state || {};

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const validationSchema = Yup.object().shape({
        roleName: Yup.string()
            .matches(/^[^\s]+(\s[^\s]+)*$/, 'Leading and trailing space is not allowed')
            .required('This field is required')
            .matches(/^[a-zA-Z0-9\s]*$/, 'Role Name must contain only alphanumeric values')
    });

    const formik = useFormik({
        validationSchema,
        initialValues: {
            id: '',
            roleName: ''
        },
        onSubmit: async (data) => {
            const user = JSON.parse(localStorage.getItem('user') || '{}');

            const mappedPermissions =
                allPermissions?.flatMap((module) => {
                    const isAnySelected = module.permissions.some((permission) => selectedPermissionIds.some((p) => p.modulePermissionId === permission.id));

                    if (!isAnySelected) return [];

                    return module.permissions.map((permission) => ({
                        modulePermissionId: permission.id,
                        isAllowed: selectedPermissionIds.some((p) => p.modulePermissionId === permission.id)
                    }));
                }) || [];

            const editData = {
                id: data?.id,
                roleName: data?.roleName,
                modifiedBy: user?.data?.userId,
                permissions: mappedPermissions
            };

            const response = await dispatch(handlePutRequest(editData, endPoints.updateRole, true));
            if (response?.status === 200) {
                formik.resetForm();
                navigate('/roles-permissions');
            }
        }
    });

    const getAllPermissions = async () => {
        const response = await handleGetRequest(endPoints.getallPermissions, true, false, dispatch);
        if (response) {
            setAllPermissions(response?.data);
        }
    };

    const getRoleById = async () => {
        setLoadingBtn(true);
        const response = await handleGetRequest(endPoints.getRoleById(rowData.id), true);
        if (response) {
            formik.setFieldValue('id', response?.data?.id);
            formik.setFieldValue('roleName', response?.data?.roleName);

            const selectedPermissions = response?.data?.permissions || [];

            const mappedPermissions = selectedPermissions
                .filter((item) => item.isAllowed === true)
                .map((item) => ({
                    modulePermissionId: item.permissionId,
                    isAllowed: true
                }));

            setSelectedPermissionIds(mappedPermissions);
        }
        setLoadingBtn(false);
    };

    useEffect(() => {
        getAllPermissions();
    }, []);

    useEffect(() => {
        if (rowData) {
            getRoleById();
        }
    }, [rowData]);

    const handleCheckboxChange = (id) => {
        setSelectedPermissionIds((prev) => {
            const exists = prev.find((p) => p.modulePermissionId === id);
            if (exists) {
                return prev.filter((p) => p.modulePermissionId !== id);
            } else {
                return [...prev, { modulePermissionId: id, isAllowed: true }];
            }
        });
    };

    const handleSelectAll = (modulePermissions) => {
        const modulePermissionIds = modulePermissions.map((p) => p.id);
        const allSelected = modulePermissionIds.every((id) => selectedPermissionIds?.some((p) => p.modulePermissionId === id));

        setSelectedPermissionIds((prev) => {
            if (allSelected) {
                return prev.filter((p) => !modulePermissionIds.includes(p.modulePermissionId));
            } else {
                const newSelected = [...prev];
                modulePermissionIds.forEach((id) => {
                    if (!newSelected.some((p) => p.modulePermissionId === id)) {
                        newSelected.push({ modulePermissionId: id, isAllowed: true });
                    }
                });
                return newSelected;
            }
        });
    };

    const handleCancel = () => {
        navigate('/roles-permissions');
    };

    return (
        <div className="p-4">
            <div className="flex justify-content-between align-items-center mb-3">
                <h3>Edit Role</h3>
            </div>

            <form onSubmit={formik.handleSubmit}>
                <div className="grid p-fluid">
                    <div className="col-12 md:col-4">
                        <div className="field mb-3">
                            <label htmlFor="roleName">
                                Role Name <span style={{ color: 'red' }}>*</span>
                            </label>
                            <InputText id="roleName" name="roleName" placeholder="Enter Role Name" value={formik?.values?.roleName} maxLength={30} onChange={formik.handleChange} />
                            {formik.touched.roleName && formik.errors.roleName && <small className="p-error">{formik.errors.roleName}</small>}
                        </div>
                    </div>

                    <div className="col-12">
                        {allPermissions?.map((module) => {
                            const modulePermissionIds = module?.permissions.map((p) => p.id);
                            const allSelected = modulePermissionIds.every((id) => selectedPermissionIds?.some((p) => p.modulePermissionId === id));

                            return (
                                <div key={module?.moduleId} className="mb-4">
                                    <div className="flex align-items-center gap-2">
                                        <input type="checkbox" checked={allSelected} onChange={() => handleSelectAll(module?.permissions)} />
                                        <p className="text-2xl font-semibold m-0">{module?.moduleName}</p>
                                    </div>
                                    <div style={{ paddingLeft: '20px' }}>
                                        {module?.permissions?.map((permission) => (
                                            <div key={permission.id}>
                                                <label>
                                                    <input type="checkbox" value={permission?.id} checked={selectedPermissionIds?.some((p) => p.modulePermissionId === permission?.id)} onChange={() => handleCheckboxChange(permission?.id)} />
                                                    {permission?.permissionName}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex justify-content-center mt-4 gap-2">
                    <Button label="Update" icon="pi pi-pencil" type="submit" disabled={loadingBtn} className="p-button-success p-button-sm" style={{ minWidth: 90, maxWidth: 120 }} />
                    <Button label="Cancel" icon="pi pi-times" type="button" className="p-button-secondary p-button-sm" style={{ minWidth: 90, maxWidth: 120 }} onClick={handleCancel} />
                </div>
            </form>
        </div>
    );
};

export default EditRole;
